import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { MyModal, MyButton, myToaster } from '@interstellar-component'
import PaymentMethodPicker from '@src/pages/Company/components/PaymentMethodPicker'
import PaymentQr from '@src/components/PaymentQr'
import { IS_PROD } from '@src/runtimeConfig'
import { formatToCurrencyIDR } from '@src/services/Helper'
import { myToasterFromApi } from '@src/services/Helper'

const STEP = { METHOD: 'method', VA: 'va' }

/**
 * Confirm payment for ONE enquiry. Shown only when quota does not cover it, so
 * the customer must pay: from Everest saldo (no PPN — saldo was taxed at top-up)
 * or via a gateway VA (subtotal + PPN, its own invoice).
 *
 * It does not create the enquiry itself — the slider owns that (attachments,
 * KTP). `createFn(paymentFields)` runs the create with the chosen method and
 * returns the response; a gateway response carries `gateway_payment` (VA number /
 * redirect) which this modal then displays.
 */
export default function ConfirmEnquiryPaymentModal({
  open = false, companyId = null, preview = null, onClose, createFn, onFinishGateway,
}) {
  const [step, setStep] = useState(STEP.METHOD)
  const [method, setMethod] = useState('saldo') // 'saldo' | wire code
  const [methodFeeIdr, setMethodFeeIdr] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [gateway, setGateway] = useState(null) // gateway_payment from create

  useEffect(() => {
    if (!open) return
    setStep(STEP.METHOD)
    setMethod('saldo')
    setMethodFeeIdr(0)
    setGateway(null)
  }, [open, companyId])

  if (!open) return null

  const price = Number(preview?.price_idr || 0)
  const taxAmount = Number(preview?.tax_amount_idr || 0)
  const isGateway = method !== 'saldo'
  // Saldo consumes balance already taxed at top-up → no PPN. Gateway is fresh
  // money in → fee + PPN both show here. PPN was previously omitted, so the Total
  // understated what is actually charged (Everest #z94x8e0y5z).
  const total = isGateway ? price + Number(methodFeeIdr || 0) + taxAmount : price
  // A rendered QR or VA number means the portal is the payment UI; the gateway
  // page degrades to a dev-only simulate button (same rule as va_number alone).
  const gwInPortal = Boolean(gateway?.va_number || gateway?.qr_string || gateway?.qr_url)

  const confirm = async () => {
    setSubmitting(true)
    try {
      const paymentFields = isGateway
        ? { payment_method: 'gateway', payment_methods: [method] }
        : { payment_method: 'saldo' }
      const res = await createFn(paymentFields)
      const body = res?.data ?? res
      const gp = body?.gateway_payment
      if (isGateway && gp) {
        setGateway(gp)
        setStep(STEP.VA)
      }
      // saldo path: the slider closed itself (createFn closeOnDone).
    } catch (err) {
      myToasterFromApi(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <MyModal open={open} onClose={onClose} maxWidth={480} forceBlur>
      <div className="max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-shadows/shadow-xl">
        {step === STEP.METHOD ? (
          <>
            <h2 className="text-lg-semibold text-gray/900">Confirm payment</h2>
            <p className="mt-1 text-sm-regular text-gray/600">
              Your quota is used up — this enquiry needs to be paid. Choose a method.
            </p>

            <div className="mt-4 rounded-xl border border-gray/200">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm-regular text-gray/700">Enquiry report</span>
                <span className="text-sm-medium text-gray/900">{formatToCurrencyIDR(price)}</span>
              </div>
              {isGateway && methodFeeIdr > 0 ? (
                <div className="flex items-center justify-between border-t border-gray/200 px-4 py-3">
                  <span className="text-sm-regular text-gray/700">App fee</span>
                  <span className="text-sm-medium text-gray/900">{formatToCurrencyIDR(methodFeeIdr)}</span>
                </div>
              ) : null}
              {isGateway && taxAmount > 0 ? (
                <div className="flex items-center justify-between border-t border-gray/200 px-4 py-3">
                  <span className="text-sm-regular text-gray/700">PPN</span>
                  <span className="text-sm-medium text-gray/900">{formatToCurrencyIDR(taxAmount)}</span>
                </div>
              ) : null}
              <div className="flex items-center justify-between border-t border-gray/200 px-4 py-3">
                <span className="text-sm-medium text-gray/900">Total</span>
                <span className="text-sm-semibold text-gray/900">{formatToCurrencyIDR(total)}</span>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3">
              {/* Everest saldo — no PPN. */}
              <button
                type="button"
                onClick={() => { setMethod('saldo'); setMethodFeeIdr(0) }}
                className={`flex items-center justify-between rounded-xl border p-3 text-left ${
                  !isGateway ? 'border-brand/600 bg-brand/50' : 'border-gray/200 hover:bg-gray/50'
                }`}
              >
                <span className="text-sm-medium text-gray/900">Everest saldo</span>
                <span className={`flex size-4 items-center justify-center rounded-full border ${
                  !isGateway ? 'border-brand/600 bg-brand/600' : 'border-gray/300'
                }`}>
                  {!isGateway ? <span className="size-1.5 rounded-full bg-white" /> : null}
                </span>
              </button>

              <PaymentMethodPicker
                companyId={companyId}
                amountIdr={price || null}
                value={isGateway ? method : null}
                // Everest saldo is the default and a real choice here, so the
                // picker must not auto-select a gateway method on mount — that
                // silently overrode saldo (every enquiry ended up gateway-funded).
                autoSelect={false}
                onChange={(wire, m) => {
                  setMethod(wire)
                  setMethodFeeIdr(Number(m?.app_fee_idr || 0))
                }}
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <MyButton color="secondary" variant="outlined" size="md" type="button" onClick={onClose} disabled={submitting}>
                <span className="text-sm-semibold">Cancel</span>
              </MyButton>
              <MyButton color="primary" variant="filled" size="md" type="button" onClick={confirm} disabled={submitting}>
                <span className="text-sm-semibold">{submitting ? 'Processing…' : 'Pay'}</span>
              </MyButton>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg-semibold text-gray/900">Complete payment</h2>
            <p className="mt-1 text-sm-regular text-gray/600">
              The enquiry is sent to CLIK automatically once the payment is verified.
            </p>
            {gateway?.qr_string || gateway?.qr_url ? (
              <div className="mt-4">
                <PaymentQr qrString={gateway?.qr_string} qrUrl={gateway?.qr_url} />
              </div>
            ) : null}
            <div className="mt-4 rounded-xl border border-gray/200">
              {gateway?.va_number ? (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm-regular text-gray/700">
                    {gateway.va_bank ? `${gateway.va_bank} VA number` : 'VA number'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(String(gateway.va_number))
                      myToaster({ status: 200, message: 'VA number copied' })
                    }}
                    className="text-sm-semibold text-gray/900 hover:text-brand/700"
                  >
                    {gateway.va_number}
                  </button>
                </div>
              ) : null}
              <div className="flex items-center justify-between border-t border-gray/200 px-4 py-3">
                <span className="text-sm-medium text-gray/900">Total</span>
                <span className="text-sm-semibold text-gray/900">
                  {formatToCurrencyIDR(gateway?.breakdown?.total_idr ?? total)}
                </span>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              {gateway?.payment_url && (!gwInPortal || !IS_PROD) ? (
                <MyButton
                  color={gwInPortal ? 'secondary' : 'primary'}
                  variant={gwInPortal ? 'outlined' : 'filled'}
                  size="md"
                  type="button"
                  onClick={() => window.open(gateway.payment_url, '_blank', 'noopener')}
                >
                  <span className="text-sm-semibold">
                    {gwInPortal ? 'Simulate payment (dev)' : 'Open payment page'}
                  </span>
                </MyButton>
              ) : null}
              <MyButton color="primary" variant="filled" size="md" type="button" onClick={onFinishGateway}>
                <span className="text-sm-semibold">Done</span>
              </MyButton>
            </div>
          </>
        )}
      </div>
    </MyModal>
  )
}

ConfirmEnquiryPaymentModal.propTypes = {
  open: PropTypes.bool,
  companyId: PropTypes.string,
  preview: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  createFn: PropTypes.func.isRequired,
  onFinishGateway: PropTypes.func.isRequired,
}
